import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap, GeoJSON } from 'react-leaflet';
import { getDistance } from 'geolib';
import { useNavigate } from 'react-router-dom';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import { WASTE_CATEGORIES, getCategoryColor, getCategoryIcon } from '../config/wasteCategories';
import { format, subMonths } from 'date-fns';
import api from '../config/api';
import L from 'leaflet';
import { FaFilter, FaSearch, FaLocationArrow, FaArrowsAlt, FaBullseye, FaPlus, FaMap, FaGlobe, FaCheckCircle, FaClock, FaBuilding, FaLock, FaTrashAlt } from 'react-icons/fa';
import ReportWasteModal from '../components/ReportWasteModal';
import MissedBinModal from '../components/MissedBinModal';
import WasteDetailModal from '../components/WasteDetailModal';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './Map.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const createColoredIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 25px; height: 25px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>`,
    iconSize: [25, 25],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24]
  });
};

const createBlackMarker = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: #333; width: 20px; height: 20px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 2px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3); opacity: 0.5;"></div>`,
    iconSize: [20, 20],
    iconAnchor: [10, 20],
    popupAnchor: [0, -20]
  });
};

const createCleanedMarker = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: white; width: 25px; height: 25px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 3px solid #10b981; box-shadow: 0 2px 5px rgba(0,0,0,0.3);"></div>`,
    iconSize: [25, 25],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24]
  });
};

const createFoodDonationMarker = (claimed) => {
  const bg = claimed ? '#9ca3af' : '#22c55e';
  const badge = claimed ? '<div style="position:absolute;top:-6px;right:-6px;background:#ef4444;color:white;font-size:8px;font-weight:bold;padding:1px 4px;border-radius:8px;white-space:nowrap;">CLAIMED</div>' : '';
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="position:relative;"><div style="background-color: ${bg}; width: 28px; height: 28px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); display:flex; align-items:center; justify-content:center; font-size:16px;">🍲</div>${badge}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14]
  });
};

const createCharityItemMarker = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: #8b5cf6; width: 28px; height: 28px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3); display:flex; align-items:center; justify-content:center; font-size:16px;">🎁</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
    popupAnchor: [0, -14]
  });
};

const createExpiredFoodMarker = () => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: #d1d5db; width: 24px; height: 24px; border-radius: 50%; border: 2px solid #9ca3af; box-shadow: 0 2px 5px rgba(0,0,0,0.2); display:flex; align-items:center; justify-content:center; font-size:13px; opacity:0.5;">🍲</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    popupAnchor: [0, -12]
  });
};

const createClusterCustomIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';
  let color = '#10b981';
  
  if (count >= 50) {
    size = 'large';
    color = '#ef4444';
  } else if (count >= 20) {
    size = 'medium';
    color = '#f59e0b';
  }
  
  const sizes = { small: 40, medium: 50, large: 60 };
  const px = sizes[size];
  
  return L.divIcon({
    html: `<div style="background-color: ${color}; width: ${px}px; height: ${px}px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold; font-size: ${px/3}px; border: 3px solid white; box-shadow: 0 3px 10px rgba(0,0,0,0.3);">${count}</div>`,
    className: 'custom-cluster-icon',
    iconSize: L.point(px, px, true),
  });
};

function MarkerClusterGroup({ children, reports, onMarkerDoubleClick, getMarkerIcon }) {
  const map = useMap();
  const clusterGroupRef = useRef(null);

  useEffect(() => {
    if (!map) return;
    
    if (clusterGroupRef.current) {
      map.removeLayer(clusterGroupRef.current);
    }

    const markerClusterGroup = L.markerClusterGroup({
      iconCreateFunction: createClusterCustomIcon,
      maxClusterRadius: 60,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      disableClusteringAtZoom: 16
    });

    reports.forEach(report => {
      if (!report.location) return;
      
      const marker = L.marker([report.location.lat, report.location.lng], {
        icon: getMarkerIcon(report)
      });
      
      let extraInfo = '';
      if (report.category === 'food_donation') {
        const claimedBadge = report.claimed_status === 'claimed' ? '<span style="background:#ef4444;color:white;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:bold;">CLAIMED</span>' :
          report.claimed_status === 'expired' ? '<span style="background:#6b7280;color:white;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:bold;">EXPIRED</span>' :
          '<span style="background:#22c55e;color:white;padding:2px 6px;border-radius:4px;font-size:11px;font-weight:bold;">AVAILABLE</span>';
        extraInfo = `<p>${claimedBadge}</p>`;
        if (report.eat_by) extraInfo += `<p><strong>Eat by:</strong> ${format(new Date(report.eat_by), 'MMM dd, HH:mm')}</p>`;
        if (report.collection_deadline) extraInfo += `<p><strong>Collect by:</strong> ${format(new Date(report.collection_deadline), 'MMM dd, HH:mm')}</p>`;
      } else if (report.category === 'charity_items' && report.item_condition) {
        const stars = '★'.repeat(report.item_condition) + '☆'.repeat(5 - report.item_condition);
        extraInfo = `<p><strong>Condition:</strong> <span style="color:#8b5cf6;">${stars}</span></p>`;
      }

      const popupContent = `
        <div class="marker-popup-enhanced">
          <h4>${WASTE_CATEGORIES[report.category]?.name || 'Unknown'}</h4>
          <p><strong>Type:</strong> ${report.wasteType || 'Unknown'}</p>
          ${extraInfo}
          <p><strong>Status:</strong> <span class="status-${report.status}">${report.status}</span></p>
          <p><strong>Date:</strong> ${format(new Date(report.timestamp), 'MMM dd, yyyy HH:mm')}</p>
          ${report.photoURL ? `<img src="${report.photoURL}" alt="Waste" class="popup-image" />` : ''}
          <p class="popup-hint">Double-click marker for full details</p>
        </div>
      `;
      marker.bindPopup(popupContent);
      marker.on('dblclick', () => onMarkerDoubleClick(report));
      markerClusterGroup.addLayer(marker);
    });

    map.addLayer(markerClusterGroup);
    clusterGroupRef.current = markerClusterGroup;

    return () => {
      if (clusterGroupRef.current) {
        map.removeLayer(clusterGroupRef.current);
      }
    };
  }, [map, reports, getMarkerIcon, onMarkerDoubleClick]);

  return null;
}

function DraggableCircle({ center, radius, draggable, onDrag }) {
  const circleRef = useRef(null);
  
  const moveIconSvg = `
    <svg viewBox="0 0 24 24" width="20" height="20" fill="white">
      <path d="M12 2l3 3h-2v4h4v-2l3 3-3 3v-2h-4v4h2l-3 3-3-3h2v-4H7v2l-3-3 3-3v2h4V5H9l3-3z"/>
    </svg>
  `;
  const dotIcon = L.divIcon({
    className: 'center-dot',
    html: `<div style="width: 36px; height: 36px; background-color: #10b981; border: 3px solid white; border-radius: 50%; box-shadow: 0 3px 8px rgba(0,0,0,0.4); cursor: grab; display: flex; align-items: center; justify-content: center;">${moveIconSvg}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18]
  });

  const eventHandlers = {
    drag: (e) => {
      const latlng = e.target.getLatLng();
      if (circleRef.current) {
        circleRef.current.setLatLng(latlng);
      }
    },
    dragend: (e) => {
      const latlng = e.target.getLatLng();
      onDrag({ lat: latlng.lat, lng: latlng.lng });
    }
  };

  return (
    <>
      <Circle
        ref={circleRef}
        center={[center.lat, center.lng]}
        radius={radius}
        pathOptions={{
          color: '#10b981',
          fillColor: '#10b981',
          fillOpacity: 0.15,
          weight: 2
        }}
      />
      {draggable && (
        <Marker 
          position={[center.lat, center.lng]} 
          icon={dotIcon} 
          draggable={true}
          eventHandlers={eventHandlers}
        />
      )}
    </>
  );
}

function ChangeView({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

function BoundaryZoomController({ boundaryData, filterMode }) {
  const map = useMap();
  const currentMinZoom = useRef(null);
  const boundaryIdRef = useRef(null);
  
  useEffect(() => {
    if (filterMode === 'boundary' && boundaryData?.geojson) {
      const newBoundaryId = boundaryData.id || boundaryData.name;
      
      // Only refit if boundary actually changed
      if (boundaryIdRef.current !== newBoundaryId) {
        boundaryIdRef.current = newBoundaryId;
        
        try {
          // Reset min zoom first to allow proper fitting
          map.setMinZoom(1);
          
          // Ensure map knows its size
          map.invalidateSize();
          
          const geoJsonLayer = L.geoJSON(boundaryData.geojson);
          const bounds = geoJsonLayer.getBounds();
          
          if (bounds.isValid()) {
            // Fit bounds with minimal padding and no animation for consistency
            map.fitBounds(bounds, { 
              padding: [10, 10],
              animate: false,
              maxZoom: 18
            });
            
            // Wait for map to stabilize then lock zoom
            setTimeout(() => {
              map.invalidateSize();
              map.fitBounds(bounds, { 
                padding: [10, 10],
                animate: false,
                maxZoom: 18
              });
              
              setTimeout(() => {
                const currentZoom = map.getZoom();
                currentMinZoom.current = currentZoom;
                map.setMinZoom(currentZoom);
              }, 100);
            }, 200);
          }
        } catch (e) {
          console.error('Error fitting bounds:', e);
        }
      }
    } else if (filterMode === 'radius') {
      map.setMinZoom(1);
      currentMinZoom.current = null;
      boundaryIdRef.current = null;
    }
  }, [boundaryData, filterMode, map]);
  
  return null;
}

const Map = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [wasteReports, setWasteReports] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState([51.505, -0.09]);
  const [circleCenter, setCircleCenter] = useState({ lat: 51.505, lng: -0.09 });
  const [radius, setRadius] = useState(2000);
  const [circleDraggable, setCircleDraggable] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [radiusMenuOpen, setRadiusMenuOpen] = useState(false);
  const [timeRangeMenuOpen, setTimeRangeMenuOpen] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState(3);
  const [loading, setLoading] = useState(true);
  const [searchAddress, setSearchAddress] = useState('');
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [missedBinModalOpen, setMissedBinModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [grayscaleMap, setGrayscaleMap] = useState(false);
  
  const [boundaries, setBoundaries] = useState([]);
  const [selectedBoundary, setSelectedBoundary] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [filterMode, setFilterMode] = useState('radius');
  const [boundaryMenuOpen, setBoundaryMenuOpen] = useState(false);
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [showBoundaryOutline, setShowBoundaryOutline] = useState(true);
  const [boundarySearchTerm, setBoundarySearchTerm] = useState('');

  const userType = currentUser?.role === 'subscriber' ? 'corporate' : 'individual';
  const subscriberType = currentUser?.subscription?.type;
  const userBoundaryId = currentUser?.subscription?.boundaryId;

  const radiusOptions = [
    { value: 500, label: '500m' },
    { value: 1000, label: '1km' },
    { value: 2000, label: '2km' },
    { value: 5000, label: '5km' },
    { value: 10000, label: '10km' },
    { value: 25000, label: '25km' },
    { value: 50000, label: '50km' }
  ];

  const timeRangeOptions = [
    { value: 3, label: '3 Months' },
    { value: 6, label: '6 Months' },
    { value: 12, label: '12 Months' },
    { value: 24, label: '24 Months' },
    { value: 36, label: '36 Months' }
  ];

  const statusOptions = [
    { value: 'all', label: 'All Reports', icon: <FaGlobe /> },
    { value: 'pending', label: 'Pending Only', icon: <FaClock /> },
    { value: 'cleaned', label: 'Cleaned Only', icon: <FaCheckCircle /> }
  ];

  useEffect(() => {
    const fetchBoundaries = async () => {
      try {
        const response = await fetch('/api/boundaries');
        if (response.ok) {
          const data = await response.json();
          setBoundaries(data);
          
          if (userType === 'corporate' && subscriberType === 'authority' && userBoundaryId) {
            setSelectedBoundary(userBoundaryId);
            setFilterMode('boundary');
            const userBoundary = data.find(b => b.id === userBoundaryId);
            if (userBoundary) {
              setMapCenter([userBoundary.center.lat, userBoundary.center.lng]);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching boundaries:', error);
      }
    };
    fetchBoundaries();
  }, [userType, subscriberType, userBoundaryId]);

  useEffect(() => {
    const loadPreferences = () => {
      const saved = localStorage.getItem('mapPreferences');
      if (saved) {
        try {
          const prefs = JSON.parse(saved);
          if (prefs.radius) setRadius(prefs.radius);
          if (prefs.statusFilter) setStatusFilter(prefs.statusFilter);
          
          // Restore filterMode for non-authority users
          if (userType !== 'corporate' || (userType === 'corporate' && subscriberType !== 'authority')) {
            if (prefs.filterMode) setFilterMode(prefs.filterMode);
          }
          
          // Restore boundary selection for corporate non-authority users
          if (prefs.selectedBoundary && userType === 'corporate' && subscriberType !== 'authority') {
            setSelectedBoundary(prefs.selectedBoundary);
            if (prefs.filterMode === 'boundary') setFilterMode('boundary');
          }
        } catch (e) {
          console.error('Error loading preferences:', e);
        }
      }
    };
    loadPreferences();
  }, [userType, subscriberType]);

  const savePreferences = useCallback(() => {
    const prefs = { radius, statusFilter, filterMode, selectedBoundary };
    localStorage.setItem('mapPreferences', JSON.stringify(prefs));
  }, [radius, statusFilter, filterMode, selectedBoundary]);

  useEffect(() => {
    savePreferences();
  }, [radius, statusFilter, filterMode, selectedBoundary, savePreferences]);

  const fetchReports = useCallback(async () => {
    try {
      let url = '/api/waste-reports/filtered?';
      const params = new URLSearchParams();
      
      if (statusFilter !== 'all') {
        params.append('status', statusFilter);
      }
      
      if (filterMode === 'boundary' && selectedBoundary) {
        params.append('boundaryId', selectedBoundary);
      } else if (filterMode === 'radius' && circleCenter) {
        params.append('radius', radius);
        params.append('lat', circleCenter.lat);
        params.append('lng', circleCenter.lng);
      }
      
      const response = await fetch(url + params.toString());
      if (response.ok) {
        const reports = await response.json();
        const normalizedReports = reports.map(report => ({
          ...report,
          location: report.location ? {
            lat: report.location.lat ?? report.location.latitude,
            lng: report.location.lng ?? report.location.longitude
          } : null
        })).filter(report => report.location && !isNaN(report.location.lat) && !isNaN(report.location.lng));
        setWasteReports(normalizedReports);
      }
    } catch (error) {
      console.error('Error fetching waste reports:', error);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, filterMode, selectedBoundary, radius, circleCenter]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(location);
          if (filterMode !== 'boundary') {
            setMapCenter([location.lat, location.lng]);
            setCircleCenter(location);
          }
          fetchReports();
        },
        (error) => {
          console.error('Error getting location:', error);
          fetchReports();
        }
      );
    }
  }, [fetchReports, filterMode]);

  useEffect(() => {
    fetchReports();
    const interval = setInterval(fetchReports, 30000);
    return () => clearInterval(interval);
  }, [fetchReports]);

  const handleReportClick = () => {
    if (!currentUser) {
      toast.info('Please sign in or create an account to report waste');
      navigate('/login?from=report');
      return;
    }
    setReportModalOpen(true);
  };

  const handleMarkerDoubleClick = (report) => {
    setSelectedReport(report);
    setDetailModalOpen(true);
  };

  const handleReportCreated = () => {
    fetchReports();
  };

  const filteredReports = wasteReports.filter((report) => {
    const reportDate = new Date(report.timestamp);
    const cutoffDate = subMonths(new Date(), selectedTimeRange);
    if (reportDate < cutoffDate) return false;
    if (selectedCategories.length > 0 && !selectedCategories.includes(report.category)) {
      return false;
    }
    return true;
  });

  const reportsWithDistance = filteredReports.map((report) => {
    let isInside = true;
    if (filterMode === 'radius' && report.location && circleCenter) {
      const distance = getDistance(
        { latitude: circleCenter.lat, longitude: circleCenter.lng },
        { latitude: report.location.lat, longitude: report.location.lng }
      );
      isInside = distance <= radius;
    }
    return { ...report, isInside };
  });

  const toggleCategory = (categoryKey) => {
    if (selectedCategories.includes(categoryKey)) {
      setSelectedCategories(selectedCategories.filter(c => c !== categoryKey));
    } else {
      setSelectedCategories([...selectedCategories, categoryKey]);
    }
  };

  const handleSearch = async () => {
    if (!searchAddress.trim()) return;
    
    // Search for local authority by name
    const searchTerm = searchAddress.toLowerCase().trim();
    const matchedBoundary = boundaries.find(b => 
      b.name.toLowerCase().includes(searchTerm) ||
      searchTerm.includes(b.name.toLowerCase())
    );
    
    if (matchedBoundary) {
      setSelectedBoundary(matchedBoundary.id);
      setFilterMode('boundary');
      setMapCenter([matchedBoundary.center.lat, matchedBoundary.center.lng]);
      setBoundaryMenuOpen(false);
      toast.success(`Showing ${matchedBoundary.name}`);
    } else {
      toast.warning('No matching local authority found');
    }
  };

  const handleCurrentLocation = () => {
    if (userLocation) {
      setMapCenter([userLocation.lat, userLocation.lng]);
      setCircleCenter(userLocation);
    } else if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(location);
        setMapCenter([location.lat, location.lng]);
        setCircleCenter(location);
      });
    }
  };

  const toggleCircleDraggable = () => {
    setCircleDraggable(!circleDraggable);
  };

  const handleCircleDrag = useCallback((newCenter) => {
    setCircleCenter(newCenter);
  }, []);

  const handleBoundaryChange = (boundaryId) => {
    setSelectedBoundary(boundaryId);
    setFilterMode('boundary');
    setBoundaryMenuOpen(false);
    
    const boundary = boundaries.find(b => b.id === boundaryId);
    if (boundary) {
      setMapCenter([boundary.center.lat, boundary.center.lng]);
    }
  };

  const handleRadiusModeSelect = () => {
    setFilterMode('radius');
    setBoundaryMenuOpen(false);
    if (userLocation) {
      setMapCenter([userLocation.lat, userLocation.lng]);
    }
  };

  const insideReports = reportsWithDistance.filter(r => r.isInside);
  const pendingCount = filteredReports.filter(r => r.status === 'pending').length;
  const cleanedCount = filteredReports.filter(r => r.status === 'cleaned').length;

  const selectedBoundaryData = boundaries.find(b => b.id === selectedBoundary);

  const canSelectBoundary = userType === 'corporate' && subscriberType !== 'authority';
  const isAuthorityUser = userType === 'corporate' && subscriberType === 'authority';

  return (
    <div className="map-enhanced-page">
      <div className="map-controls-top">
        <div className="search-bar-enhanced">
          <input
            type="text"
            placeholder="Search local authority..."
            value={searchAddress}
            onChange={(e) => setSearchAddress(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch} className="search-btn">
            <FaSearch />
          </button>
        </div>
      </div>

      {filterOpen && (
        <div className="category-filter-enhanced">
          <div className="filter-header">
            <h3>Filter by Category</h3>
            <button onClick={() => setFilterOpen(false)} className="close-btn">×</button>
          </div>
          <div className="category-checkboxes-enhanced">
            {Object.keys(WASTE_CATEGORIES).map((key) => (
              <label key={key} className="category-checkbox-item">
                <input
                  type="checkbox"
                  checked={selectedCategories.includes(key)}
                  onChange={() => toggleCategory(key)}
                />
                <span style={{ color: getCategoryColor(key) }}>
                  {getCategoryIcon(key)} {WASTE_CATEGORIES[key].name}
                </span>
              </label>
            ))}
          </div>
          <button className="clear-filters-btn" onClick={() => setSelectedCategories([])}>
            Clear All Filters
          </button>
        </div>
      )}

      <div className="map-stats-enhanced">
        <div className="stat-item-enhanced">
          <strong>{filteredReports.length}</strong>
          <span>Total</span>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item-enhanced pending">
          <strong>{pendingCount}</strong>
          <span>Pending</span>
        </div>
        <div className="stat-divider"></div>
        <div className="stat-item-enhanced cleaned">
          <strong>{cleanedCount}</strong>
          <span>Cleaned</span>
        </div>
        {filterMode === 'radius' && (
          <>
            <div className="stat-divider"></div>
            <div className="stat-item-enhanced">
              <strong>{radiusOptions.find(r => r.value === radius)?.label}</strong>
              <span>Radius</span>
            </div>
          </>
        )}
        {filterMode === 'boundary' && selectedBoundaryData && (
          <>
            <div className="stat-divider"></div>
            <div className="stat-item-enhanced boundary">
              <strong>{selectedBoundaryData.name}</strong>
              <span>Area</span>
            </div>
          </>
        )}
      </div>

      <div className={`map-container-enhanced ${grayscaleMap ? 'grayscale-map' : ''}`}>
        {loading ? (
          <div className="spinner"></div>
        ) : (
          <MapContainer
            center={mapCenter}
            zoom={13}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
          >
            <ChangeView center={mapCenter} zoom={13} />
            <BoundaryZoomController boundaryData={selectedBoundaryData} filterMode={filterMode} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />

            {filterMode === 'radius' && (
              <DraggableCircle
                center={circleCenter}
                radius={radius}
                draggable={circleDraggable}
                onDrag={handleCircleDrag}
              />
            )}

            {filterMode === 'boundary' && showBoundaryOutline && selectedBoundaryData && (
              <GeoJSON
                key={selectedBoundary}
                data={selectedBoundaryData.geojson}
                style={{
                  color: '#166534',
                  weight: 4,
                  fillColor: '#166534',
                  fillOpacity: 0.3
                }}
              />
            )}

            {userLocation && (
              <Marker position={[userLocation.lat, userLocation.lng]}>
                <Popup>Your Location</Popup>
              </Marker>
            )}

            <MarkerClusterGroup
              reports={reportsWithDistance}
              onMarkerDoubleClick={handleMarkerDoubleClick}
              getMarkerIcon={(report) => {
                if (report.category === 'food_donation') {
                  if (report.claimed_status === 'expired') return createExpiredFoodMarker();
                  return createFoodDonationMarker(report.claimed_status === 'claimed');
                }
                if (report.category === 'charity_items') {
                  return createCharityItemMarker();
                }
                if (report.status === 'cleaned') {
                  return createCleanedMarker();
                } else if (report.isInside) {
                  return createColoredIcon(getCategoryColor(report.category));
                } else {
                  return createBlackMarker();
                }
              }}
            />
          </MapContainer>
        )}

        <div className="left-controls">
          <div className="control-group">
            <button
              className="search-location-btn"
              onClick={() => setBoundaryMenuOpen(!boundaryMenuOpen)}
              title="Select area filter"
            >
              <FaSearch size={16} />
              <span>
                {filterMode === 'boundary' && selectedBoundaryData 
                  ? selectedBoundaryData.name 
                  : 'Search location'}
              </span>
            </button>
            {boundaryMenuOpen && (
              <div className="control-dropdown boundary-dropdown">
                {!isAuthorityUser && (
                  <div
                    className={`dropdown-item ${filterMode === 'radius' ? 'active' : ''}`}
                    onClick={handleRadiusModeSelect}
                  >
                    <FaBullseye /> Radius Mode
                  </div>
                )}
                <div className="dropdown-divider">Local Authorities</div>
                <div className="dropdown-search">
                  <input
                    type="text"
                    placeholder="Filter authorities..."
                    value={boundarySearchTerm}
                    onChange={(e) => setBoundarySearchTerm(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                </div>
                <div className="dropdown-items-scroll">
                  {boundaries
                    .filter(b => b.name.toLowerCase().includes(boundarySearchTerm.toLowerCase()))
                    .map((boundary) => (
                      <div
                        key={boundary.id}
                        className={`dropdown-item ${selectedBoundary === boundary.id ? 'active' : ''}`}
                        onClick={() => handleBoundaryChange(boundary.id)}
                      >
                        <FaBuilding /> {boundary.name}
                      </div>
                    ))}
                </div>
              </div>
            )}
          </div>

          {filterMode === 'radius' && (
            <>
              <div className="control-group">
                <button
                  className="control-btn green"
                  onClick={() => setRadiusMenuOpen(!radiusMenuOpen)}
                >
                  <FaBullseye size={24} />
                  <span className="control-label">{radiusOptions.find(r => r.value === radius)?.label}</span>
                </button>
                {radiusMenuOpen && (
                  <div className="control-dropdown">
                    {radiusOptions.map((option) => (
                      <div
                        key={option.value}
                        className={`dropdown-item ${radius === option.value ? 'active' : ''}`}
                        onClick={() => {
                          setRadius(option.value);
                          setRadiusMenuOpen(false);
                        }}
                      >
                        {option.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                className={`control-btn ${circleDraggable ? 'green' : 'gray'}`}
                onClick={toggleCircleDraggable}
                title="Toggle circle dragging"
              >
                <FaArrowsAlt size={24} />
                <span className="control-label">{circleDraggable ? 'ON' : 'OFF'}</span>
              </button>
            </>
          )}

          <button
            className="control-btn green"
            onClick={handleCurrentLocation}
            title="Go to current location"
          >
            <FaLocationArrow size={24} />
          </button>
        </div>

        <div className="right-controls">
          <div className="control-group">
            <button
              className="control-btn green"
              onClick={() => setStatusMenuOpen(!statusMenuOpen)}
              title="Filter by status"
            >
              {statusFilter === 'pending' ? <FaClock size={24} /> : 
               statusFilter === 'cleaned' ? <FaCheckCircle size={24} /> : 
               <FaGlobe size={24} />}
              <span className="control-label">
                {statusFilter === 'all' ? 'All' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
              </span>
            </button>
            {statusMenuOpen && (
              <div className="control-dropdown-right">
                {statusOptions.map((option) => (
                  <div
                    key={option.value}
                    className={`dropdown-item ${statusFilter === option.value ? 'active' : ''}`}
                    onClick={() => {
                      setStatusFilter(option.value);
                      setStatusMenuOpen(false);
                    }}
                  >
                    {option.icon} {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            className="control-btn-time green"
            onClick={() => setTimeRangeMenuOpen(!timeRangeMenuOpen)}
          >
            <div className="time-value">{selectedTimeRange}</div>
            <div className="time-label">mths</div>
          </button>
          {timeRangeMenuOpen && (
            <div className="control-dropdown-right">
              {timeRangeOptions.map((option) => (
                <div
                  key={option.value}
                  className={`dropdown-item ${selectedTimeRange === option.value ? 'active' : ''}`}
                  onClick={() => {
                    setSelectedTimeRange(option.value);
                    setTimeRangeMenuOpen(false);
                  }}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}

          <button
            className="control-btn green"
            onClick={() => setFilterOpen(!filterOpen)}
            title="Filter categories"
          >
            <FaFilter size={24} />
          </button>

          <button
            className={`control-btn ${grayscaleMap ? 'gray' : 'green'}`}
            onClick={() => setGrayscaleMap(!grayscaleMap)}
            title={grayscaleMap ? "Switch to color map" : "Switch to grayscale map"}
          >
            <FaMap size={24} />
            <span className="control-label">{grayscaleMap ? 'B&W' : 'CLR'}</span>
          </button>
        </div>

        <div className="bottom-left-controls">
          <button
            className="report-waste-btn"
            onClick={handleReportClick}
            title="Add New Waste"
          >
            <FaPlus size={18} />
            <span>Add New Waste</span>
          </button>
          {currentUser && (
            <button
              className="missed-bin-btn"
              onClick={() => setMissedBinModalOpen(true)}
              title="Report Missed Bin Collection"
            >
              <FaTrashAlt size={16} />
              <span>Missed Bin</span>
            </button>
          )}
        </div>
      </div>

      <ReportWasteModal
        isOpen={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        userLocation={userLocation}
        onReportCreated={handleReportCreated}
      />

      {currentUser && (
        <MissedBinModal
          isOpen={missedBinModalOpen}
          onClose={() => setMissedBinModalOpen(false)}
        />
      )}

      <WasteDetailModal
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        wasteReport={selectedReport}
      />
    </div>
  );
};

export default Map;
